<template>
  <div class="w-full p-2">
    <Tabs v-model:activeKey="activeView" class="mile-share-view-tabs">
      <TabPane key="charts" tab="Charts">
        <Button
          type="primary"
          size="large"
          class="btn-gradient"
          @click="() => (connectedCharts = ~connectedCharts)"
        >
          <span v-if="connectedCharts">Disconnect Charts</span>
          <span v-else>Connect Charts</span>
        </Button>
        <div class="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-10 items-end">
          <div v-if="chartTypeComp == 'Initial'">
            <MainChart
              :query="initialQuery"
              :data="initialData"
              defaultType="Initial"
              initialTitle="Initial Mile Share"
              @setDate="setInitialDate"
              @updateDefaultType="updateDefaultType"
              :connectedCharts="connectedCharts"
              :showYearly="true"
            />
          </div>
          <div v-else>
            <MainChart
              :query="initialQuery"
              :data="initialData"
              :defaultYear="primaryPeriodValue"
              :defaultType="primaryChartType"
              :default-index="primaryChartIndex"
              :default-range="initialDate"
              @setDate="setPrimaryDateFromChart"
              :showInitial="true"
              :showYearly="true"
              :connectedCharts="connectedCharts"
              @updateDefaultType="updateDefaultType"
            />
          </div>
          <MainChart
            :query="filiterQuery"
            :data="filiterData"
            :defaultYear="filiterPeriodValue"
            :defaultType="filiterChartType"
            :default-index="filiterChartIndex"
            :default-range="filiterDate"
            :search-day-show="true"
            :showYearly="true"
            :connectedCharts="connectedCharts"
            @setDate="setFiliterDate"
            @updateDefaultType="setFiliterPeriodTypeFromChart"
            :startMonth="startMonth"
            :key="defaultFilterYear + '-' + startMonth"
          />
        </div>
        <ReportChart
          queryname="mileShareBehaviorStatsQuery"
          gettername="mileShareBehaviorStatsData"
          :commutersData="commutersData"
          :year="reportYear"
          title="Commute Behavior Report"
          filter="Miles"
          @setDate="setReportDate"
        />
      </TabPane>

      <TabPane key="table" tab="Table">
        <div class="mt-6 space-y-8">
          <section>
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-lg font-semibold text-primary">Mile Share Comparison</h3>
                <span class="text-sm text-gray-500">
                  {{ primaryTableTitle }} vs {{ selectedPeriodLabel }}
                </span>
              </div>
              <Select
                class="min-w-[150px]"
                :value="mileShareDisplayMode"
                :options="mileShareDisplayOptions"
                @change="setMileShareDisplayMode"
              />
            </div>
            <div class="mb-4 grid grid-cols-1 xl:grid-cols-2 gap-8">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 class="font-medium text-primary">{{ primaryTableTitle }}</h4>
                  <span class="text-sm text-gray-500">{{ totalCommutersText(primaryTableData) }}</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <Select
                    class="min-w-[150px]"
                    :value="primaryPeriodType"
                    :options="primaryPeriodOptions"
                    @change="setPrimaryPeriodType"
                  />
                  <date-picker
                    v-if="primaryPeriodType !== 'Initial' && primaryPeriodType !== 'Range'"
                    :value="primaryPeriodValue"
                    :picker="pickerForPeriod(primaryPeriodType)"
                    :allowClear="false"
                    @change="setPrimaryPeriodValue"
                  />
                  <RangePicker
                    v-if="primaryPeriodType === 'Range'"
                    :value="primaryRangeValue"
                    :allowClear="false"
                    @change="setPrimaryRange"
                  />
                </div>
              </div>
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 class="font-medium text-primary">{{ selectedPeriodLabel }}</h4>
                  <span class="text-sm text-gray-500">{{ totalCommutersText(filiterData) }}</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <Select
                    class="min-w-[150px]"
                    :value="filiterPeriodType"
                    :options="periodOptions"
                    @change="setFiliterPeriodType"
                  />
                  <date-picker
                    v-if="filiterPeriodType !== 'Range'"
                    :value="filiterPeriodValue"
                    :picker="pickerForPeriod(filiterPeriodType)"
                    :allowClear="false"
                    @change="setFiliterPeriodValue"
                  />
                  <RangePicker
                    v-if="filiterPeriodType === 'Range'"
                    :value="filiterRangeValue"
                    :allowClear="false"
                    @change="setFiliterRange"
                  />
                </div>
              </div>
            </div>
            <Table
              row-key="key"
              size="middle"
              :pagination="false"
              :columns="combinedMileSummaryColumns"
              :data-source="combinedMileTableRows"
              :loading="isCombinedMileTableLoading"
              :scroll="{ x: 1200 }"
            />
          </section>

          <section>
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-lg font-semibold text-primary">Commute Behavior Report</h3>
                <span class="text-sm text-gray-500">{{ reportYearLabel }}</span>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <Select
                  class="min-w-[180px]"
                  :value="behaviorDisplayMode"
                  :options="behaviorDisplayOptions"
                  @change="setBehaviorDisplayMode"
                />
                <date-picker
                  :value="reportYear"
                  picker="year"
                  :allowClear="false"
                  @change="setReportDate"
                />
              </div>
            </div>
            <Table
              row-key="key"
              size="middle"
              :pagination="false"
              :columns="behaviorTableColumns"
              :data-source="behaviorTableRows"
              :loading="isQueryLoading(reportQuery)"
              :scroll="{ x: 1500 }"
            />
          </section>
        </div>
      </TabPane>
    </Tabs>

    <!-- api queries  -->
    <StatsMileShareInitialQuery
      :from="initialDate[0]"
      :to="initialDate[1]"
      :key="initialDate[0] + initialDate[1]"
    />
    <StatsMileMonthQuery
      v-if="filiterDate"
      :from="filiterDate[0]"
      :to="filiterDate[1]"
      :daySearch="searchDay"
      :key="filiterDate[0] + filiterDate[1] + searchDay"
    />
    <StatsMileShareBehaviorQuery
      v-if="reportDate"
      :from="reportDate[0]"
      :to="reportDate[1]"
      :key="reportDate[0] + reportDate[1]"
    />

    <StatsMileShareCommutersQuery
      v-if="reportDate"
      :from="reportDate[0]"
      :to="reportDate[1]"
      :key="reportDate[0] + reportDate[1]"
    />
  </div>
</template>

<script>
import StatsMileShareInitialQuery from '@/services/queries/stats/StatsMileShareInitialQuery.vue';
import StatsMileShareBehaviorQuery from '@/services/queries/stats/StatsMileShareBehaviorQuery.vue';
import StatsMileMonthQuery from '@/services/queries/stats/StatsMileMonthQuery.vue';

import { MONTHES, sortAndNameMonthesAndCommuters } from '@/utils/monthes';
import {
  Button,
  DatePicker,
  RangePicker,
  Select,
  Table,
  Tabs,
  TabPane,
} from 'ant-design-vue';
import dayjs from 'dayjs';
import StatsMileShareCommutersQuery from '@/services/queries/stats/StatsMileShareCommutersQuery.vue';
import ReportChart from './charts/ReportChart.vue';
import MainChart from './charts/MainChart.vue';
import getActiveMonth from '../shared/getActiveMonth';

export default {
  components: {
    ReportChart,
    Button,
    DatePicker,
    RangePicker,
    Select,
    Table,
    Tabs,
    TabPane,
    MainChart,
    StatsMileShareInitialQuery,
    StatsMileShareBehaviorQuery,
    StatsMileMonthQuery,
    StatsMileShareCommutersQuery,
  },

  data() {
    return {
      reportYear: dayjs(),
      defaultFilterYear: dayjs(),

      reportDate: null,
      filiterDate: null,
      initialDate: [null, null],
      startMonth: 1,
      connectedCharts: false,
      activeView: 'charts',
      primaryPeriodType: 'Initial',
      primaryPeriodValue: dayjs(),
      filiterPeriodType: 'Monthly',
      filiterPeriodValue: dayjs(),
      checkLoading: false,
      filterDataChanged: false,
      chartType: 'Initial',
      mileShareDisplayMode: 'both',
      mileShareDisplayOptions: [
        { value: 'trips', label: 'Trips' },
        { value: 'percent', label: 'Percentage' },
        { value: 'both', label: 'Miles + Percentage' },
      ],
      periodOptions: [
        { value: 'Weekly', label: 'Weekly' },
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Yearly', label: 'Yearly' },
      ],
      primaryPeriodOptions: [
        { value: 'Initial', label: 'Initial' },
        { value: 'Weekly', label: 'Weekly' },
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Yearly', label: 'Yearly' },
      ],
      behaviorDisplayMode: 'both',
      behaviorDisplayOptions: [
        { value: 'trips', label: 'Trips' },
        { value: 'percent', label: 'Percentage' },
        { value: 'both', label: 'Miles + Percentage' },
      ],
    };
  },

  created() {
    const currYear = dayjs().year();
    const yearDate = [`${currYear}-01-01`, `${currYear}-12-31`];

    if (!this.reportDate) {
      this.reportDate = yearDate;
    }
    if (!this.filiterDate) {
      this.filiterDate = yearDate;
    }
    this.primaryPeriodValue = dayjs(yearDate[0]);
    this.filiterPeriodValue = dayjs(yearDate[0]);
  },

  computed: {
    initialQuery() {
      return this.$store.state.stats.mileShareInitialStatsQuery;
    },
    searchDay() {
      return this.$store.state.stats.searchDay;
    },
    initialData() {
      return this.$store.getters.mileShareInitialStatsData;
    },
    filiterQuery() {
      return this.$store.state.stats.mileShareMonthStatsQuery;
    },
    filiterData() {
      return this.$store.getters.mileShareMonthStatsData;
    },
    reportData() {
      return this.$store.getters.mileShareBehaviorStatsData;
    },
    commutersData() {
      return this.$store.getters.mileShareCommutersData;
    },
    reportQuery() {
      return this.$store.state.stats.mileShareBehaviorStatsQuery;
    },
    chartTypeComp() {
      return this.chartType;
    },
    primaryRangeValue() {
      return this.toPickerRange(this.initialDate);
    },
    filiterRangeValue() {
      return this.toPickerRange(this.filiterDate);
    },
    primaryChartType() {
      return this.primaryPeriodType;
    },
    filiterChartType() {
      return this.filiterPeriodType;
    },
    primaryChartIndex() {
      return this.getPeriodIndex(this.primaryChartType, this.primaryPeriodValue);
    },
    filiterChartIndex() {
      return this.getPeriodIndex(this.filiterChartType, this.filiterPeriodValue);
    },
    primaryTableTitle() {
      return this.primaryPeriodType === 'Initial'
        ? 'Initial Mile Share'
        : `${this.primaryPeriodType} Mile Share`;
    },
    primaryTableData() {
      return this.initialData;
    },
    primaryTableQuery() {
      return this.initialQuery;
    },
    primaryColumnPrefix() {
      return this.primaryPeriodType === 'Initial' ? 'Initial' : this.primaryPeriodType;
    },
    primaryPeriodLabel() {
      return this.primaryPeriodType === 'Initial'
        ? 'Initial Reported Modes'
        : this.formatPeriodLabel(this.primaryPeriodType, this.primaryPeriodValue, this.primaryRangeValue);
    },
    selectedPeriodLabel() {
      return this.formatPeriodLabel(this.filiterPeriodType, this.filiterPeriodValue, this.filiterRangeValue);
    },
    combinedMileTableRows() {
      return this.buildCombinedMileRows(this.primaryTableData, this.filiterData);
    },
    isCombinedMileTableLoading() {
      return (
        this.isQueryLoading(this.primaryTableQuery) || this.isQueryLoading(this.filiterQuery)
      );
    },
    behaviorTableRows() {
      return this.buildBehaviorRows(this.reportData);
    },
    combinedMileSummaryColumns() {
      const columns = [
        {
          title: 'Mode',
          dataIndex: 'label',
          key: 'label',
          sorter: (a, b) => a.label.localeCompare(b.label),
        },
      ];

      if (this.mileShareDisplayMode === 'both') {
        columns.push({
          title: this.primaryPeriodLabel,
          dataIndex: 'primaryCombinedFormatted',
          key: 'primaryCombined',
          sorter: (a, b) => (a.primaryMiles ?? 0) - (b.primaryMiles ?? 0),
        });
        columns.push({
          title: this.selectedPeriodLabel,
          dataIndex: 'selectedCombinedFormatted',
          key: 'selectedCombined',
          sorter: (a, b) => (a.selectedMiles ?? 0) - (b.selectedMiles ?? 0),
        });
        columns.push({
          title: 'Difference',
          dataIndex: 'diffCombinedFormatted',
          key: 'diffCombined',
          sorter: (a, b) => (a.milesDiff ?? 0) - (b.milesDiff ?? 0),
        });
      }

      if (this.mileShareDisplayMode === 'trips') {
        columns.push({
          title: this.primaryPeriodType === 'Initial' ? 'Initial Reported Modes' : `${this.primaryPeriodLabel} Miles`,
          dataIndex: 'primaryMilesFormatted',
          key: 'primaryMiles',
          sorter: (a, b) => (a.primaryMiles ?? 0) - (b.primaryMiles ?? 0),
        });
        columns.push({
          title: `${this.selectedPeriodLabel} Miles`,
          dataIndex: 'selectedMilesFormatted',
          key: 'selectedMiles',
          sorter: (a, b) => (a.selectedMiles ?? 0) - (b.selectedMiles ?? 0),
        });
        columns.push({
          title: 'Miles Diff',
          dataIndex: 'milesDiffFormatted',
          key: 'milesDiff',
          sorter: (a, b) => (a.milesDiff ?? 0) - (b.milesDiff ?? 0),
        });
      }

      if (this.mileShareDisplayMode === 'percent') {
        columns.push({
          title: `${this.primaryPeriodLabel} Percentage`,
          dataIndex: 'primaryShareFormatted',
          key: 'primaryShare',
          sorter: (a, b) => (a.primaryShare ?? 0) - (b.primaryShare ?? 0),
        });
        columns.push({
          title: `${this.selectedPeriodLabel} Percentage`,
          dataIndex: 'selectedShareFormatted',
          key: 'selectedShare',
          sorter: (a, b) => (a.selectedShare ?? 0) - (b.selectedShare ?? 0),
        });
        columns.push({
          title: 'Percentage Diff',
          dataIndex: 'shareDiffFormatted',
          key: 'shareDiff',
          sorter: (a, b) => (a.shareDiff ?? 0) - (b.shareDiff ?? 0),
        });
      }

      return columns;
    },
    behaviorTableColumns() {
      const columns = [
        {
          title: 'Mode',
          dataIndex: 'label',
          key: 'label',
          sorter: (a, b) => a.label.localeCompare(b.label),
        },
      ];

      if (
        (this.behaviorDisplayMode === 'trips' || this.behaviorDisplayMode === 'both') &&
        this.reportData &&
        this.reportData.initial
      ) {
        columns.push({
          title: 'Initial',
          dataIndex: 'initialFormatted',
          key: 'initial',
          sorter: (a, b) => a.initial - b.initial,
        });
      }

      MONTHES.forEach((month) => {
        const key = this.monthKey(month);

        if (this.behaviorDisplayMode === 'both') {
          columns.push({
            title: `${month} Miles (Share)`,
            dataIndex: `${key}CombinedFormatted`,
            key: `${key}Combined`,
            sorter: (a, b) => a[`${key}Value`] - b[`${key}Value`],
          });
          return;
        }

        if (this.behaviorDisplayMode === 'trips') {
          columns.push({
            title: `${month} Miles`,
            dataIndex: `${key}MilesFormatted`,
            key: `${key}Miles`,
            sorter: (a, b) => a[`${key}Value`] - b[`${key}Value`],
          });
        }

        if (this.behaviorDisplayMode === 'percent') {
          columns.push({
            title: `${month} Share`,
            dataIndex: `${key}PercentFormatted`,
            key: `${key}Percent`,
            sorter: (a, b) => a[`${key}PercentValue`] - b[`${key}PercentValue`],
          });
        }
      });

      if (this.behaviorDisplayMode === 'both') {
        columns.push({
          title: 'Total Miles (Share)',
          dataIndex: 'totalCombinedFormatted',
          key: 'totalCombined',
          sorter: (a, b) => a.total - b.total,
        });

        return columns;
      }

      if (this.behaviorDisplayMode === 'trips') {
        columns.push({
          title: 'Total Miles',
          dataIndex: 'totalFormatted',
          key: 'total',
          sorter: (a, b) => a.total - b.total,
        });
      }

      return columns;
    },
    reportYearLabel() {
      return dayjs(this.reportYear).format('YYYY');
    },
  },
  watch: {
    reportData: {
      async handler(val) {
        if (!this.checkLoading && val && !this.filterDataChanged) {
          this.checkLoading = true;
          const { firstMonth, year } = await getActiveMonth(
            val,
            this.startMonth,
            new Date().getFullYear(),
            'mile_share',
          );

          if (firstMonth) {
            this.startMonth = firstMonth;
            this.filterDataChanged = true;
          }
          if (year && year !== dayjs(this.defaultFilterYear).year()) {
            const endMon = dayjs(`${year}-${firstMonth ? firstMonth + 1 : 1}-01`)
              .endOf('month')
              .format('YYYY-MM-DD');
            this.setFiliterDate([`${year}-01-01`, endMon], 'Range');
            this.defaultFilterYear = dayjs().year(year);

            this.reportYear = dayjs().year(year);
            const currYear = dayjs().year(year).year();
            this.reportDate = [`${currYear}-01-01`, `${currYear}-12-31`];
          }
          this.checkLoading = false;
        }
      },
      deep: true,
    },
    connectedCharts(val) {
      if (val) {
        this.initialDate = this.filiterDate;
      } else {
        this.initialDate = [null, null];
      }
    },
  },
  methods: {
    getOrdinalSuffix(day) {
      const value = Number(day);
      const mod100 = value % 100;

      if (mod100 >= 11 && mod100 <= 13) return 'th';

      switch (value % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    },
    formatPeriodLabel(type, value, range = []) {
      const date = dayjs(range?.[0] || value || dayjs());

      if (type === 'Weekly') {
        return `Week of ${date.format('MMMM D')}${this.getOrdinalSuffix(date.date())}`;
      }
      if (type === 'Quarterly') {
        return `Q${Math.floor(date.month() / 3) + 1} ${date.format('YYYY')}`;
      }
      if (type === 'Yearly') {
        return date.format('YYYY');
      }
      if (type === 'Range') {
        const endDate = dayjs(range?.[1] || value || dayjs());
        return `${date.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
      }

      return date.format('MMMM YYYY');
    },
    formatMilesWithShare(miles, share) {
      return `${this.formatMiles(miles)} (${this.formatPercent(share)})`;
    },
    isQueryLoading(query) {
      return Boolean(query && (query.isFetching || query.isLoading));
    },
    pickerForPeriod(type) {
      if (type === 'Weekly') {
        return 'week';
      }
      if (type === 'Quarterly') {
        return 'quarter';
      }
      return type === 'Yearly' ? 'year' : 'month';
    },
    getPeriodDates(type, value) {
      const date = dayjs(value || dayjs());

      if (type === 'Weekly') {
        const start = date.day() === 0 ? date.subtract(6, 'day') : date.subtract(date.day() - 1, 'day');
        const end = start.add(6, 'day');

        return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
      }

      if (type === 'Yearly') {
        return [
          date.startOf('year').format('YYYY-MM-DD'),
          date.endOf('year').format('YYYY-MM-DD'),
        ];
      }

      if (type === 'Quarterly') {
        const startMonth = Math.floor(date.month() / 3) * 3;
        const start = date.month(startMonth).startOf('month');
        const end = start.add(2, 'month').endOf('month');

        return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
      }

      return [
        date.startOf('month').format('YYYY-MM-DD'),
        date.endOf('month').format('YYYY-MM-DD'),
      ];
    },
    toPickerRange(dates) {
      if (!dates || !dates[0] || !dates[1]) return [];

      return [dayjs(dates[0]), dayjs(dates[1])];
    },
    formatPickerRange(dates) {
      if (!dates || !dates[0] || !dates[1]) return null;

      return [dayjs(dates[0]).format('YYYY-MM-DD'), dayjs(dates[1]).format('YYYY-MM-DD')];
    },
    getWeekIndexFromDate(value) {
      const date = dayjs(value || dayjs()).toDate();
      const year = date.getFullYear();
      const janFirst = new Date(year, 0, 1);
      const dayOfWeek = janFirst.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const firstMonday = new Date(janFirst);
      firstMonday.setDate(janFirst.getDate() - daysToMonday);

      const selectedDayOfWeek = date.getDay();
      const daysToSelectedMonday = selectedDayOfWeek === 0 ? 6 : selectedDayOfWeek - 1;
      const selectedMonday = new Date(date);
      selectedMonday.setDate(date.getDate() - daysToSelectedMonday);

      // Use UTC day counts so DST shifts can't skew the week math
      const diffDays = Math.round(
        (Date.UTC(
          selectedMonday.getFullYear(),
          selectedMonday.getMonth(),
          selectedMonday.getDate(),
        ) -
          Date.UTC(firstMonday.getFullYear(), firstMonday.getMonth(), firstMonday.getDate())) /
          (24 * 60 * 60 * 1000),
      );

      return Math.max(0, Math.round(diffDays / 7));
    },
    getPeriodIndex(type, value) {
      const date = dayjs(value || dayjs());

      if (type === 'Weekly') {
        return this.getWeekIndexFromDate(date);
      }
      if (type === 'Quarterly') {
        return Math.floor(date.month() / 3);
      }
      if (type === 'Monthly') {
        return date.month();
      }

      return 0;
    },
    setPrimaryInputFromDates(dates, type = this.primaryPeriodType) {
      if (!dates || !dates[0]) return;

      if (type === 'Initial') {
        this.primaryPeriodType = 'Initial';
        this.chartType = 'Initial';
        return;
      }

      if (type === 'Range') {
        this.primaryPeriodType = 'Range';
      } else {
        this.primaryPeriodType = type;
      }

      this.primaryPeriodValue = dayjs(dates[0]);
      this.chartType = this.primaryPeriodType;
    },
    setFiliterInputFromDates(dates, type = this.filiterPeriodType) {
      if (!dates || !dates[0]) return;

      if (type === 'Range') {
        this.filiterPeriodType = 'Range';
      } else if (type !== 'Initial') {
        this.filiterPeriodType = type;
      }

      this.filiterPeriodValue = dayjs(dates[0]);
    },
    setPrimaryPeriodType(type) {
      this.primaryPeriodType = type;

      if (type === 'Initial') {
        this.chartType = 'Initial';
        this.setInitialDate(['2010-01-01', '2110-01-01']);
        return;
      }

      this.chartType = type;

      if (type === 'Weekly') {
        this.setPrimaryPeriodValue(dayjs());
        return;
      }

      if (type !== 'Range') {
        this.setPrimaryPeriodValue(this.primaryPeriodValue);
      }
    },
    setPrimaryPeriodValue(value) {
      const nextValue = value || dayjs();
      this.primaryPeriodValue = nextValue;
      this.chartType = this.primaryPeriodType;
      const dates = this.getPeriodDates(this.primaryPeriodType, nextValue);
      this.setInitialDate(dates, this.primaryPeriodType);

      if (this.connectedCharts) {
        this.setFiliterDate(dates, this.primaryPeriodType);
      }
    },
    setPrimaryRange(dates) {
      const nextRange = this.formatPickerRange(dates);
      if (!nextRange) return;

      this.primaryPeriodType = 'Range';
      this.chartType = 'Range';
      this.primaryPeriodValue = dayjs(nextRange[0]);
      this.setInitialDate(nextRange, 'Range');

      if (this.connectedCharts) {
        this.setFiliterDate(nextRange, 'Range');
      }
    },
    setFiliterPeriodType(type) {
      this.filiterPeriodType = type;

      if (type === 'Weekly') {
        this.setFiliterPeriodValue(dayjs());
        return;
      }

      if (type !== 'Range') {
        this.setFiliterPeriodValue(this.filiterPeriodValue);
      }
    },
    setFiliterPeriodTypeFromChart(type) {
      if (!type || type === 'Initial') return;

      this.filiterPeriodType = type;

      if (type === 'Weekly') {
        this.setFiliterPeriodValue(dayjs());
      }
    },
    setFiliterPeriodValue(value) {
      const nextValue = value || dayjs();
      this.filiterPeriodValue = nextValue;
      const dates = this.getPeriodDates(this.filiterPeriodType, nextValue);
      const startDate = dayjs(dates[0]);
      this.defaultFilterYear = startDate;
      this.startMonth = startDate.month() + 1;
      this.setFiliterDate(dates, this.filiterPeriodType);
    },
    setFiliterRange(dates) {
      const nextRange = this.formatPickerRange(dates);
      if (!nextRange) return;

      this.filiterPeriodType = 'Range';
      this.filiterPeriodValue = dayjs(nextRange[0]);
      this.defaultFilterYear = this.filiterPeriodValue;
      this.startMonth = this.filiterPeriodValue.month() + 1;
      this.setFiliterDate(nextRange, 'Range');
    },
    monthKey(month) {
      return String(month || '').toLowerCase();
    },
    toNumber(value) {
      if (Array.isArray(value)) {
        return this.getMonthlyStats({ all: value }).reduce((sum, item) => sum + item.count, 0);
      }

      if (value && typeof value === 'object') {
        if (value.trips_miles || value.trips_miles === 0) {
          return this.toNumber(value.trips_miles);
        }
        if (value.trips_count || value.trips_count === 0) {
          return this.toNumber(value.trips_count);
        }
        if (value.count || value.count === 0) {
          return this.toNumber(value.count);
        }
        return 0;
      }

      const bare = typeof value === 'string' ? value.replace(/,/g, '') : value;
      const parsed = Number(bare);

      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatMiles(value) {
      return this.toNumber(value).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
    },
    formatWholeNumber(value) {
      return this.toNumber(value).toLocaleString(undefined, {
        maximumFractionDigits: 0,
      });
    },
    formatPercent(value) {
      return `${this.toNumber(value).toFixed(1)}%`;
    },
    totalCommutersText(data) {
      if (!data || (!data.no_commuters && data.no_commuters !== 0)) {
        return '0 Commuters';
      }

      return `${this.formatWholeNumber(data.no_commuters)} Commuters`;
    },
    getMonthlyStats(modeData) {
      const months = sortAndNameMonthesAndCommuters(modeData?.all);

      return months.map((monthData) => {
        const month = Object.keys(monthData).find((key) => key !== 'commuters');

        return {
          month,
          count: this.toNumber(monthData?.[month]),
          commuters: this.toNumber(monthData?.commuters),
        };
      });
    },
    rowsToMap(rows) {
      return new Map(rows.map((row) => [row.key, row]));
    },
    buildCombinedMileRows(primaryData, selectedData) {
      const primaryMap = this.rowsToMap(this.buildMileRows(primaryData));
      const selectedMap = this.rowsToMap(this.buildMileRows(selectedData));
      const keys = new Set([...primaryMap.keys(), ...selectedMap.keys()]);

      return [...keys]
        .map((key) => {
          const primary = primaryMap.get(key);
          const selected = selectedMap.get(key);
          const primaryMiles = primary?.miles ?? 0;
          const selectedMiles = selected?.miles ?? 0;
          const primaryShare = primary?.share ?? 0;
          const selectedShare = selected?.share ?? 0;
          const milesDiff = primaryMiles - selectedMiles;
          const shareDiff = primaryShare - selectedShare;

          return {
            key,
            label: primary?.label || selected?.label || key,
            primaryMiles,
            primaryMilesFormatted: this.formatMiles(primaryMiles),
            primaryShare,
            primaryShareFormatted: this.formatPercent(primaryShare),
            primaryCombinedFormatted: this.formatMilesWithShare(primaryMiles, primaryShare),
            selectedMiles,
            selectedMilesFormatted: this.formatMiles(selectedMiles),
            selectedShare,
            selectedShareFormatted: this.formatPercent(selectedShare),
            selectedCombinedFormatted: this.formatMilesWithShare(selectedMiles, selectedShare),
            milesDiff,
            milesDiffFormatted: this.formatSigned(milesDiff, this.formatMiles),
            shareDiff,
            shareDiffFormatted: this.formatSigned(shareDiff, this.formatPercent),
            diffCombinedFormatted: `${this.formatSigned(milesDiff, this.formatMiles)} (${this.formatSigned(shareDiff, this.formatPercent)})`,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    buildMileRows(data) {
      if (!data) return [];

      const rows = [];

      Object.entries(data).forEach(([key, val]) => {
        if (key === 'no_commuters' || key === 'custom_destructed') return;

        const monthlyStats = this.getMonthlyStats(val);
        const miles = monthlyStats.reduce((sum, item) => sum + item.count, 0);

        rows.push({
          key,
          label: key,
          miles,
        });
      });

      if (data.custom_destructed) {
        Object.entries(data.custom_destructed).forEach(([key, val]) => {
          rows.push({
            key: `custom-${key}`,
            label: `${key} - CM*`,
            miles: this.toNumber(val),
          });
        });
      }

      const totalMiles = rows.reduce((sum, item) => sum + item.miles, 0);

      return rows.map((row) => {
        const share = totalMiles ? (row.miles / totalMiles) * 100 : 0;

        return {
          ...row,
          share,
          milesFormatted: this.formatMiles(row.miles),
          shareFormatted: this.formatPercent(share),
        };
      });
    },
    getInitialBehaviorValue(initialData, mode) {
      if (!initialData || (!initialData[mode] && initialData[mode] !== 0)) {
        return 0;
      }

      const modeData = initialData[mode];

      if (modeData?.all || modeData?.all === 0) {
        return this.toNumber(modeData.all);
      }

      return this.toNumber(modeData);
    },
    getBehaviorMonthTotals(data) {
      if (!data) return {};

      const totals = {};

      Object.entries(data).forEach(([key, val]) => {
        if (key === 'no_commuters' || key === 'initial' || key === 'custom_destructed') {
          return;
        }

        this.getMonthlyStats(val).forEach(({ month, count }) => {
          const keyName = this.monthKey(month);
          totals[keyName] = (totals[keyName] || 0) + count;
        });
      });

      return totals;
    },
    buildBehaviorRows(data) {
      if (!data) return [];

      const monthTotals = this.getBehaviorMonthTotals(data);
      const overallTotal = Object.values(monthTotals).reduce((sum, value) => sum + value, 0);

      return Object.entries(data).reduce((rows, [key, val]) => {
        if (key === 'no_commuters' || key === 'initial' || key === 'custom_destructed') {
          return rows;
        }

        const row = {
          key,
          label: key,
          initial: this.getInitialBehaviorValue(data.initial, key),
        };
        let total = 0;

        row.initialFormatted = this.formatMiles(row.initial);

        this.getMonthlyStats(val).forEach(({ month, count }) => {
          const keyName = this.monthKey(month);
          const monthTotal = monthTotals[keyName] || 0;
          const percent = monthTotal ? (count / monthTotal) * 100 : 0;

          row[`${keyName}Value`] = count;
          row[`${keyName}MilesFormatted`] = this.formatMiles(count);
          row[`${keyName}PercentValue`] = percent;
          row[`${keyName}PercentFormatted`] = this.formatPercent(percent);
          row[`${keyName}CombinedFormatted`] = this.formatTripsWithShare(count, percent);
          total += count;
        });

        const totalPercent = overallTotal ? (total / overallTotal) * 100 : 0;
        row.total = total;
        row.totalFormatted = this.formatMiles(total);
        row.totalCombinedFormatted = this.formatTripsWithShare(total, totalPercent);
        rows.push(row);

        return rows;
      }, []);
    },
    setReportDate(y) {
      this.reportYear = y;
      const currYear = dayjs(y).year();
      const reportDate = [`${currYear}-01-01`, `${currYear}-12-31`];
      this.reportDate = reportDate;
    },
    setFiliterDate(dates, type = this.filiterPeriodType) {
      this.filiterDate = dates;
      this.setFiliterInputFromDates(dates, type);
      if (this.connectedCharts) {
        this.initialDate = dates;
      }
    },
    setInitialDate(dates, type = this.primaryPeriodType) {
      this.initialDate = dates;
      this.setPrimaryInputFromDates(dates, type);
    },
    setPrimaryDateFromChart(dates, type = this.primaryPeriodType) {
      this.setInitialDate(dates, type);
      if (this.connectedCharts) {
        this.setFiliterDate(dates, type);
      }
    },
    setMileShareDisplayMode(value) {
      this.mileShareDisplayMode = value;
    },
    updateDefaultType(type) {
      if (type !== 'Initial') {
        this.primaryPeriodType = type;
        this.primaryPeriodValue =
          type === 'Weekly'
            ? dayjs()
            : dayjs(this.initialDate?.[0] || this.filiterDate?.[0] || dayjs());
        if (type !== 'Range') {
          this.initialDate = this.getPeriodDates(type, this.primaryPeriodValue);
        }
        this.chartType = type;
        return;
      }

      this.initialDate = ['2010-01-01', '2110-01-01'];
      this.primaryPeriodType = 'Initial';
      this.chartType = type;
    },
    formatSigned(value, formatter) {
      const numeric = this.toNumber(value);
      if (!Number.isFinite(numeric)) return '0';
      const sign = numeric > 0 ? '' : '';
      return `${sign}${formatter(Math.abs(numeric))}`;
    },
    setBehaviorDisplayMode(value) {
      this.behaviorDisplayMode = value;
    },
    formatTripsWithShare(trips, percent) {
      return `${this.formatMiles(trips)} (${this.formatPercent(percent)})`;
    },
  },
};
</script>
